// src/modules/leads/pages/LeadManagement.jsx
// Admin/manager view: all leads with filters, bulk assignment, create, open detail.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Row, Col, Button, Select, Input, Space, message,
  Modal, Form, Radio,
} from 'antd';
import { ReloadOutlined, SearchOutlined, UserAddOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchBranches } from '../../branches/api';
import {
  fetchLeads, assignLeads, fetchAssignableCsrs, createLead, fetchLeadTypes,
  fetchLeadSources, fetchLeadStatuses,
} from '../api';
import { STATUS_OPTIONS, tagFor } from '../constants';
import LeadDetailPanel from '../components/LeadDetailPanel';

const { Title, Text } = Typography;

export default function LeadManagement() {
  const { can } = usePermissions();
  const canAssign = can('leadManagement', 'assign');
  const canCreate = can('leadManagement', 'create');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: null, leadType: null, interestLevel: null, source: null, unassigned: null, overdue: null, selfCollected: null, branchId: null });
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const [branches, setBranches] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [csrs, setCsrs] = useState([]);

  // code → { name, color } lookup for rendering the dynamic Status tag.
  const statusMap = Object.fromEntries(leadStatuses.map((s) => [s.code, s]));

  const [activeId, setActiveId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [assigning, setAssigning] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {});
    fetchLeadTypes().then((r) => setLeadTypes(r?.data?.data || [])).catch(() => {});
    fetchLeadSources().then((r) => setLeadSources(r?.data?.data || [])).catch(() => {});
    fetchLeadStatuses().then((r) => setLeadStatuses(r?.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== '') params[k] = v; });
      const res = await fetchLeads(params);
      setData(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load leads'); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [filters]);

  const openLead = (id) => { setActiveId(id); setDrawerOpen(true); };

  const openAssign = async () => {
    if (!selectedKeys.length) { message.warning('Select leads to assign'); return; }
    // Load CSRs for the branch of the first selected lead (or the filter branch).
    const branchId = filters.branchId || data.find((l) => l._id === selectedKeys[0])?.branch?._id;
    try {
      const res = await fetchAssignableCsrs(branchId ? { branchId } : {});
      setCsrs(res.data.data || []);
    } catch { setCsrs([]); }
    assignForm.resetFields();
    assignForm.setFieldsValue({ strategy: 'round_robin' });
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    try {
      const v = await assignForm.validateFields();
      setAssigning(true);
      await assignLeads({
        leadIds: selectedKeys,
        strategy: v.strategy,
        assignToId: v.strategy === 'manual' ? v.assignToId : undefined,
        branchId: filters.branchId || undefined,
      });
      message.success('Leads assigned');
      setAssignOpen(false); setSelectedKeys([]); load();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Assign failed');
    } finally { setAssigning(false); }
  };

  const submitCreate = async () => {
    try {
      const v = await createForm.validateFields();
      setSaving(true);
      await createLead({ ...v });
      message.success('Lead created');
      setCreateOpen(false); createForm.resetFields(); load();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Create failed');
    } finally { setSaving(false); }
  };

  const columns = [
    { title: 'Ref', dataIndex: 'reference', render: (v) => <Text code>{v}</Text> },
    { title: 'Name', dataIndex: 'name', render: (v, r) => <a onClick={() => openLead(r._id)}>{v}</a> },
    { title: 'Phone', dataIndex: 'phone', render: (v, r) => <span>{v}{r.altPhone ? ` / ${r.altPhone}` : ''}</span> },
    { title: 'Type', dataIndex: 'leadType', render: (v) => v ? <Tag>{v}</Tag> : '—' },
    { title: 'Branch', dataIndex: ['branch', 'name'], render: (v) => v || '—' },
    { title: 'Assigned', dataIndex: ['assignedTo', 'name'], render: (v) => v || <Tag color="orange">Unassigned</Tag> },
    { title: 'Next Call', dataIndex: 'nextCallDate', render: (v) => v ? dayjs(v).format('DD/MM/YY') : '—' },
    { title: 'Status', dataIndex: 'interestLevel', render: (v) => v ? <Tag color={statusMap[v]?.color || 'default'}>{statusMap[v]?.name || v}</Tag> : '—' },
    { title: 'Stage', dataIndex: 'status', render: (v) => <Tag color={tagFor(STATUS_OPTIONS, v).color}>{tagFor(STATUS_OPTIONS, v).label}</Tag> },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Lead Management</Title></Col>
        <Col>
          <Space>
            {canAssign && <Button icon={<UserAddOutlined />} disabled={!selectedKeys.length} onClick={openAssign}>Assign ({selectedKeys.length})</Button>}
            {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setCreateOpen(true); }}>New Lead</Button>}
            <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input allowClear prefix={<SearchOutlined />} placeholder="Search name / phone" style={{ width: 220 }}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} onPressEnter={() => load(1)} />
          <Select allowClear placeholder="Stage" style={{ width: 160 }} value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))} options={STATUS_OPTIONS} />
          <Select allowClear placeholder="Lead status" style={{ width: 160 }} value={filters.interestLevel}
            onChange={(v) => setFilters((f) => ({ ...f, interestLevel: v || null }))}
            options={leadStatuses.map((s) => ({ value: s.code, label: <Tag color={s.color || 'default'}>{s.name}</Tag> }))} />
          <Select allowClear placeholder="Lead source" style={{ width: 160 }} value={filters.source}
            onChange={(v) => setFilters((f) => ({ ...f, source: v || null }))} options={leadSources.map((s) => ({ value: s.code, label: s.name }))} />
          <Select allowClear placeholder="Lead type" style={{ width: 160 }} value={filters.leadType}
            onChange={(v) => setFilters((f) => ({ ...f, leadType: v || null }))} options={leadTypes.map((t) => ({ value: t.code, label: t.name }))} />
          <Select allowClear placeholder="Branch" style={{ width: 180 }} value={filters.branchId} showSearch optionFilterProp="children"
            onChange={(v) => setFilters((f) => ({ ...f, branchId: v || null }))}>
            {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
          </Select>
          <Select allowClear placeholder="Quick filter" style={{ width: 160 }}
            value={filters.unassigned ? 'unassigned' : filters.overdue ? 'overdue' : filters.selfCollected ? 'self_collected' : null}
            onChange={(v) => setFilters((f) => ({
              ...f,
              unassigned: v === 'unassigned' ? true : null,
              overdue: v === 'overdue' ? true : null,
              selfCollected: v === 'self_collected' ? true : null,
            }))}
            options={[
              { value: 'unassigned', label: 'Unassigned' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'self_collected', label: 'Self-collected' },
            ]} />
        </Space>
      </Card>

      <Table rowKey="_id" size="small" columns={columns} dataSource={data} loading={loading}
        rowSelection={canAssign ? { selectedRowKeys: selectedKeys, onChange: setSelectedKeys } : undefined}
        pagination={{ current: page, pageSize: 50, total, showTotal: (t) => `${t} leads`, onChange: (p) => { setPage(p); load(p); } }}
        scroll={{ x: 1000 }} />

      <LeadDetailPanel leadId={activeId} open={drawerOpen} onClose={() => setDrawerOpen(false)} onChanged={load} />

      <Modal title={`Assign ${selectedKeys.length} lead(s)`} open={assignOpen} onCancel={() => setAssignOpen(false)}
        onOk={submitAssign} okButtonProps={{ loading: assigning }} destroyOnClose>
        <Form form={assignForm} layout="vertical">
          <Form.Item name="strategy" label="Strategy" initialValue="round_robin">
            <Radio.Group>
              <Radio value="round_robin">Round-robin across CSRs</Radio>
              <Radio value="manual">Assign to one CSR</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.strategy !== c.strategy}>
            {({ getFieldValue }) => getFieldValue('strategy') === 'manual' && (
              <Form.Item name="assignToId" label="CSR" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="children" placeholder="Select CSR">
                  {csrs.map((c) => <Select.Option key={c._id} value={c._id}>{c.name}{c.employeeCode ? ` (${c.employeeCode})` : ''}</Select.Option>)}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
          {!csrs.length && <Text type="warning">No CSRs found for this branch. Ensure users with a CSR role exist in the branch.</Text>}
        </Form>
      </Modal>

      <Modal title="New Lead" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={submitCreate} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={createForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="Phone" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="altPhone" label="Alternate Phone"><Input /></Form.Item></Col>
            <Col span={24}><Form.Item name="address" label="Address"><Input.TextArea rows={2} placeholder="Optional" /></Form.Item></Col>
            <Col span={12}><Form.Item name="leadType" label="Lead Type"><Select allowClear placeholder="Select type" options={leadTypes.map((t) => ({ value: t.code, label: t.name }))} /></Form.Item></Col>
            <Col span={12}><Form.Item name="source" label="Source"><Select allowClear placeholder="Select source" options={leadSources.map((s) => ({ value: s.code, label: s.name }))} /></Form.Item></Col>
            <Col span={12}><Form.Item name="interestLevel" label="Status"><Select allowClear placeholder="Hot / Cold / …" options={leadStatuses.map((s) => ({ value: s.code, label: <Tag color={s.color || 'default'}>{s.name}</Tag> }))} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
